import { API_BASE } from './http'
import type {
  BridgeStatus,
  Job,
  Notification,
  PaymentSettled,
  UserFile,
  UserMaterial,
} from '@/types/api'

type Frame =
  | { type: 'file'; data: UserFile }
  | { type: 'job'; data: Job }
  | { type: 'bridge'; data: BridgeStatus }
  | { type: 'notification'; data: Notification }
  | { type: 'material'; data: UserMaterial }
  | { type: 'credits'; data: number }
  | { type: 'payment'; data: PaymentSettled }
  | { type: 'resync' }

function socketUrl(): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws'
  return `${scheme}://${location.host}${API_BASE}/events`
}

const RETRY_BASE_MS = 1_000
const RETRY_MAX_MS = 30_000
const LIVENESS_MS = 60_000
const LIVENESS_CHECK_MS = 15_000

export interface EventHandlers {
  file?: (file: UserFile) => void
  job?: (job: Job) => void
  bridge?: (status: BridgeStatus) => void
  notification?: (notification: Notification) => void
  material?: (material: UserMaterial) => void
  credits?: (credits: number) => void
  payment?: (settled: PaymentSettled) => void
  resync?: () => void
  reconnected?: () => void
}

export function subscribe(handlers: EventHandlers): () => void {
  let socket: WebSocket | null = null
  let retry: ReturnType<typeof setTimeout> | null = null
  let attempt = 0
  let opened = false
  let closed = false
  let lastFrame = Date.now()

  const dispatch = (raw: string): void => {
    lastFrame = Date.now()
    let frame: Frame
    try {
      frame = JSON.parse(raw) as Frame
    } catch {
      return
    }
    if (frame.type === 'file') handlers.file?.(frame.data)
    else if (frame.type === 'job') handlers.job?.(frame.data)
    else if (frame.type === 'bridge') handlers.bridge?.(frame.data)
    else if (frame.type === 'notification') handlers.notification?.(frame.data)
    else if (frame.type === 'material') handlers.material?.(frame.data)
    else if (frame.type === 'credits') handlers.credits?.(frame.data)
    else if (frame.type === 'payment') handlers.payment?.(frame.data)
    else if (frame.type === 'resync') handlers.resync?.()
  }

  const reopen = (): void => {
    if (closed || retry !== null) return
    attempt += 1
    retry = setTimeout(
      () => {
        retry = null
        open()
      },
      Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), RETRY_MAX_MS),
    )
  }

  const detach = (target: WebSocket): void => {
    target.onopen = null
    target.onmessage = null
    target.onclose = null
    target.onerror = null
  }

  const open = (): void => {
    if (socket) {
      detach(socket)
      socket.close()
    }

    lastFrame = Date.now()
    const next = new WebSocket(socketUrl())
    socket = next

    next.onopen = () => {
      if (socket !== next) return
      attempt = 0
      lastFrame = Date.now()
      if (opened) handlers.reconnected?.()
      opened = true
    }
    next.onmessage = (e: MessageEvent<string>) => {
      if (socket !== next) return
      dispatch(e.data)
    }
    next.onclose = () => {
      if (socket !== next) return
      reopen()
    }
    next.onerror = () => next.close()
  }

  const checkLiveness = (): void => {
    if (closed || socket?.readyState !== WebSocket.OPEN) return
    if (Date.now() - lastFrame <= LIVENESS_MS) return
    socket.close()
  }

  const timer = setInterval(checkLiveness, LIVENESS_CHECK_MS)
  window.addEventListener('online', checkLiveness)
  document.addEventListener('visibilitychange', checkLiveness)
  open()

  return () => {
    closed = true
    clearInterval(timer)
    window.removeEventListener('online', checkLiveness)
    document.removeEventListener('visibilitychange', checkLiveness)
    if (retry !== null) clearTimeout(retry)
    if (socket) {
      detach(socket)
      socket.close()
    }
    socket = null
  }
}
